// Path: ./src/utils/aiImprovementSession.ts
import {
  safeSessionStorageGetJSON,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetJSON,
} from "./localStorage";

const STORAGE_KEY = "goflyer_ai_improve_pending_jobs";
/** Pending jobs older than this are discarded (ms). */
const MAX_PENDING_AGE_MS = 2 * 60 * 60 * 1000;

export interface AiImprovePendingJob {
  jobId: string;
  sourceImageId: string;
  createdAt: number;
}

type PendingJobStore = Record<string, AiImprovePendingJob>;

const readStore = (): PendingJobStore => {
  const parsed = safeSessionStorageGetJSON<PendingJobStore | null>(
    STORAGE_KEY,
    null,
  );
  return parsed && typeof parsed === "object" ? parsed : {};
};

const writeStore = (store: PendingJobStore) => {
  if (Object.keys(store).length === 0) {
    safeSessionStorageRemoveItem(STORAGE_KEY);
    return;
  }

  safeSessionStorageSetJSON(STORAGE_KEY, store);
};

const pruneExpired = (store: PendingJobStore): PendingJobStore => {
  const now = Date.now();
  const next: PendingJobStore = {};
  for (const [imageId, entry] of Object.entries(store)) {
    if (now - entry.createdAt <= MAX_PENDING_AGE_MS) {
      next[imageId] = entry;
    }
  }
  return next;
};

export const saveAiImprovePendingJob = (
  jobId: string,
  sourceImageId: string,
): void => {
  const store = pruneExpired(readStore());
  store[sourceImageId] = {
    jobId,
    sourceImageId,
    createdAt: Date.now(),
  };
  writeStore(store);
};

export const clearAiImprovePendingJob = (sourceImageId: string): void => {
  const store = readStore();
  if (!store[sourceImageId]) return;
  delete store[sourceImageId];
  writeStore(store);
};

export const clearAiImprovePendingJobByJobId = (jobId: string): void => {
  const store = readStore();
  let changed = false;
  for (const [imageId, entry] of Object.entries(store)) {
    if (entry.jobId === jobId) {
      delete store[imageId];
      changed = true;
    }
  }
  if (changed) writeStore(store);
};

export const getAiImprovePendingJob = (
  sourceImageId: string,
): AiImprovePendingJob | null => {
  const store = pruneExpired(readStore());
  writeStore(store);
  return store[sourceImageId] ?? null;
};

export const getAiImprovePendingJobsForImages = (
  sourceImageIds: string[],
): AiImprovePendingJob[] => {
  const store = pruneExpired(readStore());
  writeStore(store);
  const idSet = new Set(sourceImageIds);
  return Object.values(store)
    .filter((entry) => idSet.has(entry.sourceImageId))
    .sort((a, b) => b.createdAt - a.createdAt);
};

export const isAiImprovementNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const err = error as { status?: number; statusText?: string };
  if (err.status === 404) return true;
  if (typeof err.statusText === "string" && /not found/i.test(err.statusText)) {
    return true;
  }
  return false;
};
